import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormField } from '@ipfabric/ui-components/FormField/FormField';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { SegmentButton } from '@ipfabric/ui-components/SegmentedButton/SegmentButton/SegmentButton';
import { SegmentedButton } from '@ipfabric/ui-components/SegmentedButton/SegmentedButton';
import { isComputedReservedLabelName } from '@ipfabric/utils/labels';
import type { FormEvent } from 'react';
import type {
  Control,
  FieldErrors,
  SubmitHandler,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormReset,
  UseFormSetValue,
} from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { LabelPicker } from '../../shared/LabelPicker/LabelPicker';
import { ReservedLabelOverrideWarning } from '../../shared/ReservedLabelOverrideWarning';
import { INHERIT_LABEL, INHERIT_TOOLTIP } from '../../shared/inheritSwitchText';
import { targetTypeSupportsInherit } from '../ruleTypes';
import { RuleConditionsEditor } from './RuleConditionsEditor';
import { RuleLabelsEditor } from './RuleLabelsEditor';
import { TestRuleView } from './TestRuleView';
import { applyRuleTargetTypeChange } from './applyRuleTargetTypeChange';
import type { RuleFormSubmitValues, RuleFormValues } from './ruleFormSchema';
import type { RuleFormView } from './useRuleFormViews';
import { useRuleTargetType } from './useRuleTargetType';

type RuleFormBodyProps = {
  view: RuleFormView;
  formType: 'add' | 'edit';
  control: Control<RuleFormValues>;
  errors: FieldErrors<RuleFormValues>;
  setValue: UseFormSetValue<RuleFormValues>;
  getValues: UseFormGetValues<RuleFormValues>;
  reset: UseFormReset<RuleFormValues>;
  handleSubmit: UseFormHandleSubmit<RuleFormValues, RuleFormSubmitValues>;
  onSubmit: SubmitHandler<RuleFormSubmitValues>;
  onOpenLabelPicker: () => void;
  pickerDraftLabels: string[];
  onPickerDraftLabelsChange: (labels: string[]) => void;
  canTest: boolean;
  testSnapshot: { conditions: RuleFormValues['conditions'] } | null;
  onRunTest: () => void;
  onClearTestSnapshot: () => void;
};

const RuleFormMainFields = ({
  formType,
  control,
  errors,
  setValue,
  getValues,
  reset,
  handleSubmit,
  onSubmit,
  onOpenLabelPicker,
  onClearTestSnapshot,
}: Pick<
  RuleFormBodyProps,
  | 'formType'
  | 'control'
  | 'errors'
  | 'setValue'
  | 'getValues'
  | 'reset'
  | 'handleSubmit'
  | 'onSubmit'
  | 'onOpenLabelPicker'
  | 'onClearTestSnapshot'
>) => {
  const type = useRuleTargetType(control);
  const labels = useWatch({ control, name: 'labels' }) ?? [];

  return (
    <form
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void handleSubmit(onSubmit)(event);
      }}
    >
      <Card className='flex flex-col gap p-sm'>
        {formType === 'add' ? (
          <Controller
            control={control}
            name='type'
            render={({ field }) => (
              <FormField dataTestId='rule-form-target-type' label='Target type'>
                <SegmentedButton.Single
                  onChange={(value) => {
                    if (value !== 'device' && value !== 'intL2') {
                      return;
                    }
                    applyRuleTargetTypeChange({
                      nextType: value,
                      currentType: field.value,
                      getValues,
                      reset,
                      onClearTestSnapshot,
                    });
                  }}
                  value={field.value}
                  variant='outline'
                >
                  <SegmentButton className='flex-1' value='device'>
                    Device
                  </SegmentButton>
                  <SegmentButton className='flex-1' value='intL2'>
                    Interface
                  </SegmentButton>
                </SegmentedButton.Single>
              </FormField>
            )}
          />
        ) : null}
        <RuleConditionsEditor
          control={control}
          error={errors.conditions?.message}
        />
        <FormTextField<RuleFormValues>
          control={control}
          dataTestId='rule-form-description'
          label='Description (optional)'
          name='description'
          type='text'
        />
        <RuleLabelsEditor
          control={control}
          error={errors.labels?.message}
          onOpenPicker={onOpenLabelPicker}
          setValue={setValue}
        />
        <ReservedLabelOverrideWarning
          names={labels
            .map((label) => label.name)
            .filter(isComputedReservedLabelName)}
        />
        {targetTypeSupportsInherit(type) ? (
          <div data-testid='rule-form-inherit'>
            <FormSwitch<RuleFormValues>
              control={control}
              label={INHERIT_LABEL}
              labelTooltip={INHERIT_TOOLTIP}
              name='inherit'
            />
          </div>
        ) : null}
      </Card>
    </form>
  );
};

const RuleFormTestPanel = ({
  control,
  canTest,
  testSnapshot,
  onRunTest,
  onClearTestSnapshot,
}: Pick<
  RuleFormBodyProps,
  'control' | 'canTest' | 'testSnapshot' | 'onRunTest' | 'onClearTestSnapshot'
>) => {
  const type = useRuleTargetType(control);

  return (
    <div className='flex flex-col gap-sm'>
      <Card className='flex flex-col gap p-sm'>
        <RuleConditionsEditor
          control={control}
          onConditionsChange={onClearTestSnapshot}
        />
        <Button
          variant='outline'
          type='button'
          disabled={!canTest}
          onClick={onRunTest}
          dataTestId='rule-form-test-run'
        >
          Test rule
        </Button>
      </Card>
      {testSnapshot && (
        <TestRuleView
          key={testSnapshot.conditions
            .map(
              (condition) =>
                `${condition.property}:${condition.operator}:${condition.regex}`,
            )
            .join('|')}
          conditions={testSnapshot.conditions}
          type={type}
        />
      )}
    </div>
  );
};

export const RuleFormBody = (props: RuleFormBodyProps) => {
  switch (props.view) {
    case 'main':
      return (
        <RuleFormMainFields
          formType={props.formType}
          control={props.control}
          errors={props.errors}
          setValue={props.setValue}
          getValues={props.getValues}
          reset={props.reset}
          handleSubmit={props.handleSubmit}
          onSubmit={props.onSubmit}
          onOpenLabelPicker={props.onOpenLabelPicker}
          onClearTestSnapshot={props.onClearTestSnapshot}
        />
      );
    case 'labels':
      return (
        <LabelPicker
          onChange={props.onPickerDraftLabelsChange}
          selectedLabels={props.pickerDraftLabels}
        />
      );
    case 'test':
      return (
        <RuleFormTestPanel
          control={props.control}
          canTest={props.canTest}
          testSnapshot={props.testSnapshot}
          onRunTest={props.onRunTest}
          onClearTestSnapshot={props.onClearTestSnapshot}
        />
      );
  }
};
